import { useMemo, useState } from "react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ReferenceLine } from "recharts";
import { useModuleData } from "@/lib/useData";
import { PALETTE, COLORS, fmt, uniq, CURRENT_YEAR } from "@/lib/api";
import { PageHeader, Panel, Kpi, FilterSelect, ChartTooltip, Segmented, Loading } from "@/components/dash";
import { DataTable } from "@/components/DataTable";
import { AIInsights } from "@/components/AIInsights";
import { ProductGate } from "@/components/ProductGate";

const STATUS_COLOR = { Operating: COLORS.cyan, "Under Construction": COLORS.orange, Announced: COLORS.blue, Decommissioned: "#64748B" };
const sumCap = (rows) => rows.reduce((a, r) => a + r.capacity_kt, 0);

function CapacityView({ product, selector }) {
  const { data, isLoading } = useModuleData("capacity", product);
  const rows = useMemo(() => data?.rows || [], [data]);
  const [company, setCompany] = useState("all");
  const [region, setRegion] = useState("all");
  const [status, setStatus] = useState("all");
  const [groupBy, setGroupBy] = useState("region");

  const filtered = useMemo(() => rows.filter((r) =>
    (company === "all" || r.company === company) && (region === "all" || r.region === region) && (status === "all" || r.status === status)
  ), [rows, company, region, status]);

  const years = uniq(rows.map((r) => r.year));
  const groups = uniq(filtered.map((r) => r[groupBy]));
  const timeline = years.map((y) => {
    const o = { year: String(y) };
    groups.forEach((g) => (o[g] = sumCap(filtered.filter((r) => r.year === y && r[groupBy] === g))));
    return o;
  });

  const lastHist = CURRENT_YEAR - 1;
  const lastYear = Math.max(...years, 0);
  const leaders = uniq(filtered.map((r) => r.company))
    .map((c) => ({ company: c, [lastHist]: sumCap(filtered.filter((r) => r.company === c && r.year === lastHist)), [lastYear]: sumCap(filtered.filter((r) => r.company === c && r.year === lastYear)) }))
    .sort((a, b) => b[lastYear] - a[lastYear]);

  const plants = useMemo(() => {
    const m = {};
    filtered.forEach((r) => {
      m[r.plant] = m[r.plant] || { ...r, cap_hist: 0, cap_future: 0, start: null };
      if (r.year === lastHist) m[r.plant].cap_hist = r.capacity_kt;
      if (r.year === lastYear) m[r.plant].cap_future = r.capacity_kt;
      if (r.capacity_kt > 0 && (m[r.plant].start == null || r.year < m[r.plant].start)) m[r.plant].start = r.year;
    });
    return Object.values(m);
  }, [filtered, lastHist, lastYear]);

  const statusPie = uniq(plants.map((p) => p.status)).map((s) => ({ name: s, value: plants.filter((p) => p.status === s).length }));
  const capNow = sumCap(filtered.filter((r) => r.year === lastHist));
  const capFut = sumCap(filtered.filter((r) => r.year === lastYear));
  const cap10 = sumCap(filtered.filter((r) => r.year === years[0]));

  if (isLoading) return <Loading />;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Module C · Plant Capacity" title="Capacity Tracker" desc={`Nameplate capacity by company, plant and region — ${years[0]}–${lastHist} history and ${CURRENT_YEAR}–${lastYear} upcoming.`}>
        {selector}
        <FilterSelect testId="capacity-company-filter" value={company} onChange={setCompany} options={uniq(rows.map((r) => r.company))} allLabel="All companies" />
        <FilterSelect testId="capacity-region-filter" value={region} onChange={setRegion} options={uniq(rows.map((r) => r.region))} allLabel="All regions" />
        <FilterSelect testId="capacity-status-filter" value={status} onChange={setStatus} options={uniq(rows.map((r) => r.status))} allLabel="All statuses" />
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testId="capacity-kpi-current" label={`${lastHist} Capacity`} value={fmt(capNow / 1000, 2)} unit="Mt/yr" />
        <Kpi testId="capacity-kpi-future" label={`${lastYear} Capacity`} value={fmt(capFut / 1000, 2)} unit="Mt/yr" delta={capNow ? (capFut / capNow - 1) * 100 : null} accent={COLORS.orange} />
        <Kpi testId="capacity-kpi-10y" label="10-yr Additions" value={fmt((capNow - cap10) / 1000, 2)} unit="Mt/yr" accent={COLORS.blue} />
        <Kpi testId="capacity-kpi-plants" label="Plants Tracked" value={plants.length} unit={`${plants.filter((p) => p.status !== "Operating" && p.status !== "Decommissioned").length} upcoming`} accent={COLORS.cyan} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel
          className="lg:col-span-2"
          title="Capacity Timeline"
          sub={`kt/yr stacked by ${groupBy}`}
          testId="capacity-timeline-chart"
          right={<Segmented testId="capacity-groupby-toggle" value={groupBy} onChange={setGroupBy} options={[{ value: "region", label: "Region" }, { value: "company", label: "Company" }, { value: "chemical", label: "Product" }]} />}
        >
          <ResponsiveContainer width="100%" height={360}>
            <AreaChart data={timeline}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis dataKey="year" stroke="#334155" />
              <YAxis stroke="#334155" tickFormatter={(v) => fmt(v / 1000, 0) + " Mt"} />
              <Tooltip content={<ChartTooltip unit="kt/yr" />} />
              <Legend />
              <ReferenceLine x={String(CURRENT_YEAR)} stroke={COLORS.orange} strokeDasharray="4 4" label={{ value: "Upcoming →", fill: COLORS.orange, fontSize: 11, position: "insideTopLeft" }} />
              {groups.map((g, i) => (
                <Area key={g} type="monotone" dataKey={g} stackId="1" stroke={PALETTE[i % PALETTE.length]} fill={PALETTE[i % PALETTE.length]} fillOpacity={0.55} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <AIInsights module="capacity" product={product} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Panel className="lg:col-span-2" title="Company Leaderboard" sub={`kt/yr · ${lastHist} vs ${lastYear}`} testId="capacity-company-chart">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={leaders}>
              <CartesianGrid stroke={COLORS.grid} vertical={false} />
              <XAxis dataKey="company" stroke="#334155" interval={0} tick={{ fontSize: 10 }} />
              <YAxis stroke="#334155" />
              <Tooltip content={<ChartTooltip unit="kt/yr" />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Legend />
              <Bar dataKey={String(lastHist)} fill={COLORS.blue} radius={[3, 3, 0, 0]} />
              <Bar dataKey={String(lastYear)} fill={COLORS.orange} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Plant Status" sub="Number of plants" testId="capacity-status-pie-chart">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={statusPie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2} stroke="none">
                {statusPie.map((p, i) => <Cell key={p.name} fill={STATUS_COLOR[p.name] || PALETTE[i]} />)}
              </Pie>
              <Tooltip content={<ChartTooltip unit="plants" />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="Plant Registry" sub="All tracked plants with current and future capacity">
        <DataTable
          testId="capacity-data-table"
          rows={plants}
          columns={[
            { key: "company", label: "Company" },
            { key: "plant", label: "Plant" },
            { key: "region", label: "Region" },
            { key: "country", label: "Country" },
            { key: "chemical", label: "Product" },
            { key: "status", label: "Status", render: (r) => (
              <span className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border" style={{ color: STATUS_COLOR[r.status], borderColor: (STATUS_COLOR[r.status] || "#64748B") + "66" }}>{r.status}</span>
            ) },
            { key: "start", label: "Start", num: true },
            { key: "cap_hist", label: `${lastHist} kt/yr`, num: true, render: (r) => fmt(r.cap_hist) },
            { key: "cap_future", label: `${lastYear} kt/yr`, num: true, render: (r) => <span className="text-[#F58B23]">{fmt(r.cap_future)}</span> },
          ]}
        />
      </Panel>
    </div>
  );
}

export default function Capacity() {
  return <ProductGate module="capacity" eyebrow="Capacity Tracker" title="Capacity Tracker">{(p) => <CapacityView {...p} />}</ProductGate>;
}
